import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { copyMapping, listCopySources } from "utils/collegeDocumentService";
import { COPY_SOURCES_QUERY_KEY } from "./documentLibraryQueryKeys";
import { libraryErrorMessage } from "./libraryErrorMessage";

const NO_SOURCE_SELECTED = "";

/**
 * Skip-reason banners for a copy result. Overlap and missing-page skips get separate wording
 * because the remedy differs: an overlap means the field already exists here and needs a manual
 * decision, while a missing page means this document is shorter than the source and that field
 * has to be mapped by hand from scratch.
 */
const SkipReasons = ({ result }) => (
  <>
    {result.skippedOverlapLabels.length > 0 && (
      <Alert severity="warning">
        Skipped because they overlap a field already on this document (re-map by hand if still
        needed): {result.skippedOverlapLabels.join(", ")}.
      </Alert>
    )}
    {result.skippedMissingPageLabels.length > 0 && (
      <Alert severity="warning">
        Skipped because this document doesn&apos;t have that many pages. Map these by hand:{" "}
        {result.skippedMissingPageLabels.join(", ")}.
      </Alert>
    )}
  </>
);

const CopyResultSummary = ({ result }) => (
  <Stack spacing={1}>
    <Alert severity={result.copiedCount > 0 ? "success" : "info"}>
      Copied {result.copiedCount} field(s). Check every rectangle against the new form before
      saving.
    </Alert>
    <SkipReasons result={result} />
  </Stack>
);

const SourcePicker = ({ sources, sourceFileId, onChange }) =>
  sources.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      This college has no other mapped documents to copy from yet.
    </Typography>
  ) : (
    <TextField select fullWidth label="Copy from" value={sourceFileId} onChange={onChange}>
      {sources.map((source) => (
        <MenuItem key={source.id} value={source.id}>
          {source.name} ({source.mappedFieldCount} fields)
        </MenuItem>
      ))}
    </TextField>
  );

/** Copy a mapped field set from another of this college's documents, the yearly-revision
 * shortcut, so a form that's 95% last year's doesn't have to be remapped from scratch. */
const CopyMappingDialog = ({ open, fileId, onClose, onCopied }) => {
  const [sourceFileId, setSourceFileId] = useState(NO_SOURCE_SELECTED);
  const [result, setResult] = useState(null);

  const sourcesQuery = useQuery({
    queryKey: [COPY_SOURCES_QUERY_KEY, fileId],
    queryFn: () => listCopySources(fileId),
    enabled: open && Boolean(fileId),
  });

  const copyMutation = useMutation({
    mutationFn: () => copyMapping(fileId, Number(sourceFileId)),
    onSuccess: (data) => {
      setResult(data);
      onCopied();
    },
  });

  const handleClose = () => {
    setResult(null);
    setSourceFileId(NO_SOURCE_SELECTED);
    onClose();
  };

  const sources = sourcesQuery.data || [];

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy mapping from another document</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <SourcePicker
            sources={sources}
            sourceFileId={sourceFileId}
            onChange={(event) => setSourceFileId(event.target.value)}
          />
          {copyMutation.isError && (
            <Alert severity="error">
              {libraryErrorMessage(copyMutation.error, "Could not copy that mapping")}
            </Alert>
          )}
          {result && <CopyResultSummary result={result} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          disabled={!sourceFileId || copyMutation.isLoading}
          onClick={() => copyMutation.mutate()}
        >
          Copy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyMappingDialog;
